import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import "./styles/app.css";
import { loadTokenAtBoot } from "./native/tokenStore";
import { ensureBiometricUnlock } from "./native/biometric";
import { initNativeShell } from "./native/shell";
import { isNative } from "./native/platform";

function renderApp() {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </StrictMode>
  );
}

function renderLockScreen() {
  // Locked out (biometric cancelled/failed): a minimal retry rather than the
  // app. Reload re-runs boot() and re-prompts. Kept plain (no React) so it
  // works even before the app tree mounts.
  document.body.innerHTML =
    '<div style="display:grid;place-items:center;height:100vh;font-family:system-ui;gap:16px">' +
    '<div style="font-size:15px;color:#555">Xynex Money is locked</div>' +
    '<button id="retry" style="padding:12px 24px;font-size:16px;border:0;border-radius:8px;background:#1a5b3e;color:#fff">Unlock</button>' +
    "</div>";
  document
    .getElementById("retry")
    ?.addEventListener("click", () => location.reload());
}

async function boot() {
  // Hydrate the token cache (async on native secure storage) BEFORE the app
  // makes any API call, then gate behind biometrics on native cold-start.
  await loadTokenAtBoot();
  const unlocked = await ensureBiometricUnlock();
  if (!unlocked) {
    renderLockScreen();
    return;
  }
  renderApp();

  // Native chrome: status bar, splash-hide, hardware back button.
  initNativeShell(() => {
    if (window.history.length > 1 && location.pathname !== "/") {
      window.history.back();
      return true;
    }
    return false; // at root → allow exit-confirm
  });

  // Deep links: an opened https://money.xynexglobal.com/... link (App Links,
  // Task 8) reopens the app here — route the SPA to its path+query.
  if (isNative()) {
    const { App } = await import("@capacitor/app");
    void App.addListener("appUrlOpen", ({ url }) => {
      try {
        const u = new URL(url);
        window.history.pushState({}, "", u.pathname + u.search);
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch {
        /* ignore malformed deep link */
      }
    });
  }
}

void boot();
